import { useState } from "react";
import IconTile from "../IconTile";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Sparkles, ArrowRight, Check, Infinity as InfinityIcon, Crown, Users, Palette,
  Layers, Rocket, Bot, MessageCircle, MessagesSquare, Share2, BadgePercent, CalendarX2,
  TrendingUp, Wallet, ShieldCheck,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import HowAgenciesEarn from "./HowAgenciesEarn";
import PricingPlanCard from "./PricingPlanCard";
import PricingAddons from "./PricingAddons";
import PricingComparisonTable from "./PricingComparisonTable";
import SEO, { SITE_URL } from "../SEO";
import { Reveal, Overline } from "../Reveal";
import AnimatedNumber from "../AnimatedNumber";
import CurrencySelector from "../CurrencySelector";
import { Button } from "../ui/button";
import FaqList from "../FaqList";
import { REFUND_SUMMARY } from "../../data/pageFaqs";
import { PLANS, COMPARISON_SECTIONS, SIGNUP_URL } from "../../data/content";
import { FEATURE_CATEGORIES, AUTOMATION_CATEGORIES, SOFTWARE_CATEGORIES, featurePath } from "../../data/features";
import { CurrencyProvider, useCurrency } from "../../contexts/CurrencyContext";
import { CURRENCIES, convertFromINRStepped } from "../../data/currencies";

// Dedicated landing page for people searching "lifetime deal" - the
// Unlimited plans at their one-time (`onetime`) price only. The regular
// /pricing page is untouched; this page just re-presents the same PLANS data.

const PATH = "/white-label-ai-saas-lifetime-deal";
const FLAGSHIP_KEY = "ai-enterprise";

// Same plans, re-badged so the everything-unlimited plan is the hero here.
const LIFETIME_PLANS = PLANS.map((p) => ({
  ...p,
  badge: p.key === FLAGSHIP_KEY ? "BEST LIFETIME DEAL" : null,
}));
const FLAGSHIP = LIFETIME_PLANS.find((p) => p.key === FLAGSHIP_KEY);

const YEARS_COMPARED = 5;

// Foreign-currency pricing for this page: fair conversion + markup, rounded
// to a multiple of `step`. Raise/lower either number to tune prices abroad.
const LIFETIME_FX = { markup: 1.2, step: 50 };
const convertLifetime = (inr, currency) => convertFromINRStepped(inr, currency, LIFETIME_FX);

// India sees INR; every other country defaults to USD.
const INR = CURRENCIES.find((c) => c.code === "INR");
const USD = CURRENCIES.find((c) => c.code === "USD");
const detectLifetimeCurrency = (country) => ((country || "").toUpperCase() === "IN" ? INR : USD);

// `toLocal` converts an INR figure; `fmt` formats an already-local amount, so
// sums and differences are done on the displayed (rounded) numbers.
function useMoney() {
  const { currency } = useCurrency();
  const toLocal = (inr) => convertLifetime(inr, currency);
  const fmt = (n) => `${currency.symbol}${Math.round(n).toLocaleString("en-US")}`;
  const money = (inr) => fmt(toLocal(inr));
  return { toLocal, fmt, money };
}

// --- AI capabilities -------------------------------------------------------
// Card groups pulled from the feature pages so copy stays in one place; the
// core AI group is local because it spans several channels.
const CORE_AI = {
  id: "core-ai",
  tag: "Core AI",
  title: "AI Across Every Channel",
  accent: { color: "#FF6B00", tint: "rgba(255,107,0,0.10)" },
  points: [
    { icon: Bot, title: "AI Content Generation", desc: "On-brand posts, blogs and captions for every client in seconds - trained on their voice, not generic templates." },
    { icon: MessageCircle, title: "AI Review Replies", desc: "Human-quality replies to every Google review, as drafts for approval or fully automatic." },
    { icon: MessagesSquare, title: "AI Comments Reply", desc: "AI answers comments and DMs across Facebook and Instagram so every client stays responsive." },
    { icon: Share2, title: "Social Automation", desc: "Publish and sync across Google Business Profile, Facebook, Instagram and WordPress from one dashboard." },
  ],
};

const AI_GROUPS = [
  CORE_AI,
  ...["ai-tools", "publishing", "agency"].map((id) => FEATURE_CATEGORIES.find((c) => c.id === id)),
];

const AI_CAPABILITY_COUNT = AI_GROUPS.reduce((n, g) => n + g.points.length, 0);

// --- AI SaaS Business highlights ------------------------------------------
const FLAGSHIP_HIGHLIGHTS = [
  { icon: InfinityIcon, title: "Unlimited Channels", desc: "Google Business Profile, Facebook, Instagram, WhatsApp API CRM, WordPress, AEO + GEO and Instant Websites - no channel caps." },
  { icon: Users, title: "Unlimited Clients & Businesses", desc: "Onboard as many clients and locations as you can sell - your cost stays the same." },
  { icon: Palette, title: "100% White-Label", desc: "Your brand, logo, colours, login page and custom domain. Clients never see MagicBolt." },
  { icon: Layers, title: "Complete Ecosystem", desc: "AI automation and QR software - every product in one login." },
  { icon: Crown, title: "Agency & Reseller Tools", desc: "Client management, plans & usage limits, billing, lead center and bulk mailing built in." },
  { icon: Rocket, title: "Future AI Products Included", desc: "New AI automation products are added to your plan as they launch." },
];

// --- Earnings calculator --------------------------------------------------
// Presets reuse the "All Channels Combined" resale tiers from /how-agencies-earn.
const CHARGE_PRESETS = [
  { label: "Tier 3", amount: 15000 },
  { label: "Tier 2", amount: 20000 },
  { label: "Tier 1", amount: 30000 },
];

// --- FAQ -------------------------------------------------------------------
const LIFETIME_FAQS = [
  {
    q: "What does the lifetime deal include?",
    a: "You pay once for your chosen Unlimited plan instead of monthly or yearly billing. The AI SaaS Business plan includes unlimited AI automation channels, unlimited clients and businesses, every QR software product, and full white-label branding on your own domain.",
  },
  {
    q: "Which lifetime plan gives unlimited channels?",
    a: "AI SaaS Business. It unlocks every AI automation channel - Google Business Profile, Facebook, Instagram, WhatsApp API CRM, WordPress, AEO + GEO and Instant Websites - with no channel limits. AI Agency covers Google Business Profile, WordPress and Instant Websites; Digital Software covers QR-based software.",
  },
  {
    q: "Can I resell it under my own brand?",
    a: "Yes. Every lifetime plan is white-label: your logo, colours, login page and custom domain. You set your own prices, bill clients directly and keep the margin.",
  },
  {
    q: "What do I get besides the software?",
    a: "Every plan ships with white-label setup, custom domain setup, a dedicated launch manager, sales and proposal kits, agency training, digital marketing courses and a lead list for your city.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: `Lifetime plans come with a 30-day refund window. ${REFUND_SUMMARY}`,
  },
  {
    q: "I'd rather pay monthly or yearly. Is that possible?",
    a: "Yes. Monthly, annual and channel-based plans are on the main pricing page.",
  },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    ...LIFETIME_PLANS.map((p) => ({
      "@type": "Product",
      name: `MagicBolt AI - ${p.name} Lifetime Deal`,
      description: p.tagline,
      brand: { "@type": "Brand", name: "MagicBolt AI" },
      url: `${SITE_URL}${PATH}`,
      offers: {
        "@type": "Offer",
        name: "Lifetime (one-time payment)",
        price: p.pricing.onetime.amount,
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}${PATH}`,
      },
    })),
    {
      "@type": "FAQPage",
      mainEntity: LIFETIME_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

// ---------------------------------------------------------------------------

function SectionHeading({ overline, title, subtitle }) {
  return (
    <Reveal className="max-w-2xl mx-auto text-center">
      <Overline>{overline}</Overline>
      <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
        {title}
      </h2>
      {subtitle && <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">{subtitle}</p>}
    </Reveal>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-16 overflow-hidden bg-white border-b border-[#E6E9F1]">
      <div className="mb-blob mb-blob-orange mb-anim-a w-[380px] h-[380px] -top-24 right-0" />
      <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -left-20" />
      <div className="absolute inset-0 mb-grid-lines opacity-60" />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF6B00]/30 bg-[#FFF3E9] px-4 py-1.5 mb-6">
            <Sparkles className="mb-sparkle-icon w-3.5 h-3.5 text-[#FF6B00]" />
            <span className="text-xs font-bold text-[#FF6B00]">Lifetime Deal · Pay Once, Own It</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
            Unlimited Channels. <span className="mb-gradient-text">One Lifetime Payment.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Own a white-label AI SaaS for your agency - unlimited AI automation channels, unlimited clients and the
            complete MagicBolt ecosystem, for a single one-time payment.
          </p>

          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF6B00]/10 px-4 py-1.5 text-sm font-bold text-[#FF6B00]">
            Automate and Rank on Every Platform
          </p>
          <img
            src="/magicbolt-logo-transparent.webp"
            alt="MagicBolt AI"
            width="720"
            height="240"
            className="mt-4 mx-auto h-20 md:h-28 w-auto max-w-full"
          />
          <img
            src="/social%20icon.webp"
            alt="Automate Google Business Profile, Facebook, Instagram, WhatsApp, WordPress and more"
            className="mt-2 mx-auto h-16 md:h-24 w-auto max-w-full"
          />

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="rounded-full h-12 px-7 font-semibold bg-[#FF6B00] hover:bg-[#FF8533] text-white">
              <a href={SIGNUP_URL}>
                Claim Lifetime Deal <ArrowRight className="ml-1 w-4 h-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-7 font-semibold">
              <a href="#lifetime-plans">Compare Lifetime Plans</a>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-[#0B1533]">
            {["No Monthly Billing", "Unlimited Clients", "Your Brand & Domain", "Launch Setup Included"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[#FF6B00]" strokeWidth={3} />
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StatsStrip() {
  const stats = [
    { value: "∞", label: "AI automation channels" },
    { value: "∞", label: "Clients & businesses" },
    { value: `${AI_CAPABILITY_COUNT}+`, label: "AI capabilities" },
    { value: `${FEATURE_CATEGORIES.length}`, label: "Products in one login" },
  ];
  return (
    <section className="bg-[#0B1533]">
      <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="text-3xl md:text-4xl font-extrabold text-[#FF8533]">{s.value}</div>
            <div className="mt-1 text-xs md:text-sm font-medium text-white/70">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LifetimePlans() {
  return (
    <section id="lifetime-plans" className="relative py-20 md:py-24 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Lifetime Pricing"
          title="Pick Your Lifetime Plan"
          subtitle="Every plan below is a single one-time payment - no monthly or yearly platform billing."
        />
        <Reveal className="mt-8 flex justify-center">
          <CurrencySelector />
        </Reveal>
        <div className="mt-8 grid gap-6 lg:grid-cols-3 items-stretch">
          {LIFETIME_PLANS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.1} className="flex flex-col">
              <PricingPlanCard plan={p} view="onetime" convert={convertLifetime} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FlagshipHighlights() {
  return (
    <section className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white overflow-hidden">
      <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -bottom-32 -right-24" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="AI SaaS Agency Highlights"
          title={<>Why <span className="text-[#FF6B00]">{FLAGSHIP.name}</span> Is the Best Lifetime Deal</>}
          subtitle={FLAGSHIP.positioning}
        />

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FLAGSHIP_HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={(i % 3) * 0.08}>
              <div className="group h-full rounded-2xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/30 hover:shadow-[0_16px_36px_rgba(255,107,0,0.12)]">
                <IconTile icon={h.icon} />
                <h3 className="mt-4 text-lg font-bold text-[#0B1533]">{h.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{h.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10 rounded-xl border border-[#FF6B00]/30 bg-[#FFF3E9] p-6 md:p-8">
          <div className="text-xs font-bold uppercase tracking-wider text-[#FF6B00]">Everything in {FLAGSHIP.name}</div>
          <div className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {FLAGSHIP.businessFeatures.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm font-medium text-[#0B1533]">
                <Check className="w-4 h-4 text-[#FF6B00] shrink-0" strokeWidth={3} />
                {f}
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function LifetimeSavings() {
  const { toLocal, fmt } = useMoney();
  return (
    <section className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-[#F4F6FB]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="Lifetime vs Annual"
          title="Pay Once and Stop Paying Every Year"
          subtitle={`What each plan costs over ${YEARS_COMPARED} years on annual billing, against a single lifetime payment.`}
        />
        <Reveal delay={0.1} className="mt-12 overflow-x-auto rounded-xl border border-[#E6E9F1] bg-white mb-card-shadow">
          <table className="w-full min-w-[560px] text-sm border-collapse">
            <thead>
              <tr className="border-b border-[#E6E9F1] bg-[#F9FAFC] text-[#0B1533]">
                <th className="text-left px-5 py-4 font-bold">Plan</th>
                <th className="px-5 py-4 font-bold text-center">Annual / year</th>
                <th className="px-5 py-4 font-bold text-center">{YEARS_COMPARED} years annual</th>
                <th className="px-5 py-4 font-bold text-center">Lifetime</th>
                <th className="px-5 py-4 font-bold text-center text-[#16A34A]">You save</th>
              </tr>
            </thead>
            <tbody>
              {LIFETIME_PLANS.map((p) => {
                const yearly = toLocal(p.pricing.yearly.amount);
                const lifetime = toLocal(p.pricing.onetime.amount);
                const total = yearly * YEARS_COMPARED;
                const flagship = p.key === FLAGSHIP_KEY;
                return (
                  <tr key={p.key} className={`border-b border-[#E6E9F1] last:border-0 ${flagship ? "bg-[#FFF3E9]" : ""}`}>
                    <td className="px-5 py-4 font-bold text-[#0B1533]">
                      {p.name}
                      {flagship && <Crown className="inline ml-1.5 w-4 h-4 text-[#FF6B00] -mt-0.5" />}
                    </td>
                    <td className="px-5 py-4 text-center text-[#475569]">{fmt(yearly)}</td>
                    <td className="px-5 py-4 text-center text-[#94A3B8] line-through">{fmt(total)}</td>
                    <td className="px-5 py-4 text-center font-extrabold text-[#0B1533]">{fmt(lifetime)}</td>
                    <td className="px-5 py-4 text-center font-extrabold text-[#16A34A]">{fmt(total - lifetime)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Reveal>
        <Reveal delay={0.15} className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { icon: CalendarX2, title: "No renewals to track", desc: "One payment instead of a bill every year." },
            { icon: BadgePercent, title: "50% off right now", desc: "Lifetime prices are at a limited-time 50% discount." },
            { icon: ShieldCheck, title: "Launch support included", desc: "White-label and domain setup plus a dedicated launch manager with every plan." },
          ].map((b) => (
            <div key={b.title} className="flex gap-3 rounded-xl border border-[#E6E9F1] bg-white p-5">
              <b.icon className="w-5 h-5 text-[#FF6B00] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-[#0B1533]">{b.title}</div>
                <div className="mt-1 text-xs text-[#475569]">{b.desc}</div>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function AiCapabilities() {
  return (
    <section id="ai-capabilities" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="AI Capabilities"
          title={<>Every AI Capability, <span className="mb-gradient-text">Included for Life</span></>}
          subtitle="AI content, replies and autopilot, a composer and content calendar for every channel, and a full white-label agency platform - all in every lifetime plan."
        />

        <div className="mt-14 space-y-14">
          {AI_GROUPS.map((g) => (
            <div key={g.id}>
              <Reveal className="flex flex-wrap items-center gap-3">
                <span
                  className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
                  style={{ background: g.accent.tint, color: g.accent.color }}
                >
                  {g.tag}
                </span>
                <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-[#0B1533]">{g.title}</h3>
                {g.slug && (
                  <Link to={featurePath(g)} className="ml-auto text-sm font-semibold text-[#FF6B00] hover:underline">
                    Learn more →
                  </Link>
                )}
              </Reveal>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {g.points.map((pt, i) => (
                  <Reveal key={pt.title} delay={(i % 4) * 0.06}>
                    <div className="h-full rounded-xl border border-[#E6E9F1] bg-[#F9FAFC] p-5 transition-all hover:-translate-y-1 hover:bg-white hover:shadow-[0_8px_24px_rgba(11,21,51,0.08)]">
                      <span
                        className="grid place-items-center w-10 h-10 rounded-lg"
                        style={{ background: g.accent.tint, color: g.accent.color }}
                      >
                        <pt.icon className="w-5 h-5" />
                      </span>
                      <h4 className="mt-4 text-base font-bold text-[#0B1533]">{pt.title}</h4>
                      <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{pt.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureGroup({ title, categories }) {
  return (
    <div>
      <Reveal>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#94A3B8]">{title}</h3>
      </Reveal>
      <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((c, i) => {
          const Icon = c.accent.Icon;
          return (
            <Reveal key={c.id} delay={(i % 3) * 0.06}>
              <div className="flex h-full flex-col rounded-xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow">
                <div className="flex items-center gap-3">
                  <span
                    className="grid place-items-center w-10 h-10 rounded-lg shrink-0"
                    style={{ background: c.accent.tint, color: c.accent.color }}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">{c.tag}</div>
                    <h4 className="text-base font-bold text-[#0B1533] leading-tight">{c.title}</h4>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.intro}</p>
                <ul className="mt-4 space-y-1.5">
                  {c.points.map((pt) => (
                    <li key={pt.title} className="flex items-start gap-2 text-sm text-[#0B1533]">
                      <Check className="w-3.5 h-3.5 mt-1 text-[#FF6B00] shrink-0" strokeWidth={3} />
                      {pt.title}
                    </li>
                  ))}
                </ul>
                <Link
                  to={featurePath(c)}
                  className="mt-auto pt-5 text-sm font-semibold text-[#FF6B00] hover:underline"
                >
                  Explore {c.title} →
                </Link>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}

function AllFeatures() {
  return (
    <section id="all-features" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-[#F4F6FB] scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="All Features"
          title="Everything You Own With a Lifetime Plan"
          subtitle="Every product and feature in the MagicBolt platform, on one page."
        />
        <div className="mt-12 space-y-12">
          <FeatureGroup title="AI Automation Channels" categories={AUTOMATION_CATEGORIES} />
          <FeatureGroup title="Software & Platform" categories={SOFTWARE_CATEGORIES} />
        </div>
      </div>
    </section>
  );
}

function EarningsCalculator() {
  const { toLocal, fmt, money } = useMoney();
  const [clients, setClients] = useState(10);
  const [charge, setCharge] = useState(CHARGE_PRESETS[0].amount);

  const lifetimeCost = toLocal(FLAGSHIP.pricing.onetime.amount);
  const monthly = clients * toLocal(charge);
  const yearly = monthly * 12;
  const paybackMonths = Math.max(1, Math.ceil(lifetimeCost / monthly));

  return (
    <section id="lifetime-earnings" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white scroll-mt-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <SectionHeading
          overline="How to Earn"
          title="Your Lifetime Plan Pays for Itself"
          subtitle={`Estimate what you earn reselling ${FLAGSHIP.name} - and how fast the one-time payment comes back.`}
        />
        <Reveal className="mt-8 flex justify-center">
          <CurrencySelector />
        </Reveal>

        <Reveal delay={0.1} className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-[#E6E9F1] bg-[#F9FAFC] p-6 md:p-8">
            <label className="flex items-center justify-between text-sm font-bold text-[#0B1533]">
              Clients
              <span className="text-2xl font-extrabold text-[#FF6B00]">{clients}</span>
            </label>
            <input
              type="range"
              min={1}
              max={100}
              value={clients}
              onChange={(e) => setClients(Number(e.target.value))}
              aria-label="Number of clients"
              className="mt-3 w-full accent-[#FF6B00]"
            />

            <div className="mt-8 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-bold text-[#0B1533]">You charge each client (per month)</span>
              <span className="rounded-full bg-[#16A34A]/10 px-3 py-1 text-xs font-bold text-[#16A34A]">
                Recurring in year: {fmt(toLocal(charge) * 12)}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {CHARGE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  aria-pressed={charge === p.amount}
                  onClick={() => setCharge(p.amount)}
                  className={`rounded-lg border px-2 py-3 text-center transition-all ${
                    charge === p.amount
                      ? "border-[#FF6B00] bg-[#FF6B00] text-white shadow-[0_6px_16px_rgba(255,107,0,0.3)]"
                      : "border-[#E6E9F1] bg-white text-[#0B1533] hover:border-[#FF6B00]/40"
                  }`}
                >
                  <div className="text-[11px] font-bold uppercase tracking-wide opacity-80">{p.label}</div>
                  <div className="text-sm font-extrabold">{money(p.amount)}</div>
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#94A3B8]">
              Tiers use the "All Channels Combined" resale prices from our agency earnings guide.
            </p>
          </div>

          <div className="rounded-xl bg-[#0B1533] p-6 md:p-8 text-white">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60">
              <TrendingUp className="w-4 h-4" /> Yearly revenue
            </div>
            <AnimatedNumber
              value={yearly}
              format={fmt}
              className="mt-2 block text-4xl md:text-5xl font-extrabold tracking-tight text-[#FF8533]"
            />
            <p className="mt-2 text-xs text-white/60">
              {clients} {clients === 1 ? "client" : "clients"} × {fmt(toLocal(charge))} / month × 12 months
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-white/5 p-4">
                <div className="flex items-center gap-1.5 text-xs text-white/60">
                  <Wallet className="w-3.5 h-3.5" /> Monthly revenue
                </div>
                <div className="mt-1 text-xl font-extrabold">{fmt(monthly)}</div>
              </div>
              <div className="rounded-lg bg-white/5 p-4">
                <div className="text-xs text-white/60">Lifetime plan paid back in</div>
                <div className="mt-1 text-xl font-extrabold">
                  {paybackMonths} {paybackMonths === 1 ? "month" : "months"}
                </div>
              </div>
            </div>
            <p className="mt-6 text-xs italic text-white/50">
              Illustrative estimate only. Actual earnings depend on your pricing, clients and sales.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function LifetimeFaq() {
  return (
    <section className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>Lifetime Deal FAQ</Overline>
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
            Questions about the lifetime deal
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <FaqList
            group="lifetime-faq"
            items={LIFETIME_FAQS.map((f, i) =>
              i === LIFETIME_FAQS.length - 1
                ? {
                    ...f,
                    extra: (
                      <>
                        {" "}
                        <Link to="/pricing" className="font-semibold text-[#FF6B00] hover:underline">
                          See all pricing →
                        </Link>
                      </>
                    ),
                  }
                : f
            )}
          />
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { money } = useMoney();
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FF6B00] to-[#FF8533]">
      <div className="max-w-5xl mx-auto px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
            Own {FLAGSHIP.name} for life - {money(FLAGSHIP.pricing.onetime.amount)} one time.
          </h2>
          <p className="mt-2 text-sm md:text-base text-white/85">
            Unlimited channels, unlimited clients, your brand. 50% off for a limited time.
          </p>
        </div>
        <Button asChild className="shrink-0 rounded-full h-12 px-7 font-semibold bg-white text-[#FF6B00] hover:bg-white/90">
          <a href={SIGNUP_URL}>
            Claim Lifetime Deal <ArrowRight className="ml-1 w-4 h-4" />
          </a>
        </Button>
      </div>
    </section>
  );
}

function LifetimeDealContent() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="lifetime-deal-page">
      <SEO
        title="White-Label AI SaaS Lifetime Deal - Pay Once | MagicBolt AI"
        description="White-label AI SaaS lifetime deal for agencies: pay once for unlimited AI automation channels, unlimited clients and your own brand and domain."
        path={PATH}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Pricing", path: "/pricing" },
          { name: "Lifetime Deal", path: PATH },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>
      <Navbar />
      <main>
        <Hero />
        <StatsStrip />
        <FlagshipHighlights />
        <AiCapabilities />
        <AllFeatures />
        <PricingAddons />
        <LifetimePlans />
        <PricingComparisonTable plans={LIFETIME_PLANS} sections={COMPARISON_SECTIONS} view="onetime" convert={convertLifetime} />
        <LifetimeSavings />
        <HowAgenciesEarn />
        <EarningsCalculator />
        <FinalCta />
        <LifetimeFaq />
        <CredibilityStrip />
      </main>
      <Footer />
    </div>
  );
}

export default function LifetimeDealPage() {
  return (
    <CurrencyProvider detectCurrency={detectLifetimeCurrency}>
      <LifetimeDealContent />
    </CurrencyProvider>
  );
}
